// @ts-ignore
import { API, FeatureFlagsObject } from 'Core'
import SITE_CONFIG from 'Core/Config'
import { getAppViewPreferenceName, getLastViewForApp } from 'Utils/AuthHelpers'
import { userpreferencesSlice } from 'infra/redux/reducers'
import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector, shallowEqual } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'

export const FeatureFlagContext = React.createContext(null)

export const FeatureFlagProvider = (props) => {
  const [appReady, setAppReady] = useState(false)
  const initiated = useRef(false)
  const { publicUrl } = SITE_CONFIG
  const dispatch = useDispatch()
  const urlPrefix = publicUrl || ''
  // @ts-ignore
  const user = useSelector((state) => state.user)
  // @ts-ignore
  const userpreferences = useSelector((state) => {
    //@ts-ignore
    return Object.values(state.userpreferences.map)
  }, shallowEqual)

  // @ts-ignore
  const history = useHistory()
  // @ts-ignore
  const location = useLocation()
  const [contextValue, setContextValue] = useState({
    featureFlags: {},
    viewMode: 'user',
    loading: true
  })
  const USER_VIEW_ROUTES = new Set([
    'bundles', 'service-accounts', 'devices', 'targets', 'resources',
    'saas-apps', 'iam-roles', 'access-requests', 'policys', 'agentic-apps',
    'addomaincontrollers', 'secrets', 'activity-logs', 'session-logs',
    'passkeys', 'datacloud', 'iam-actions', 'account-resources', 'iam-policy', 'iam-action'
  ])

  const ADMIN_VIEW_ROUTES = new Set([
    'dashboard', 'bundles', 'service-accounts', 'devices', 'targets', 'resources',
    'saas-apps', 'iam-roles', 'access-requests', 'policys', 'agentic-apps',
    'addomaincontrollers', 'secrets', 'activity-logs', 'session-logs',
    'passkeys', 'datacloud', 'iam-actions', 'account-resources', 'iam-policy',
    'users', 'groups', 'gateways', 'proxy-controllers', 'server-controllers',
    'tags', 'clouds', 'integrations', 'settings', 'personal-agents', 'idaanalyzer', 'workloads',
    'agent-config', 'agents'
  ])

  /**
   * Redirects user to the same route in the next view if available, otherwise the view root.
   */
  const redirectToView = (prefferedAppView) => {
    const currentPath = window.location.pathname.toLowerCase()
    let currentSegment = ''
    for (const viewKey of ['admin', 'user']) {
      const prefix = (urlPrefix + '/' + viewKey + '/').toLowerCase()
      if (currentPath.startsWith(prefix)) {
        currentSegment = currentPath.slice(prefix.length).split('/')[0]
        break
      }
    }
    const targetRoutes = prefferedAppView === 'admin' ? ADMIN_VIEW_ROUTES : USER_VIEW_ROUTES
    if (currentSegment && targetRoutes.has(currentSegment)) {
      history.push(`/${prefferedAppView}/${currentSegment}`)
    } else {
      history.push(`/${prefferedAppView}/`)
    }
  }
  /**
   * Runs when user is logged in
   * Checks prefferedAppView and role to select view mode.
   */
  const setRoleBasedPermissions = async () => {
    if (initiated.current) return
    initiated.current = true
    /** Check if url has desired app view mentioned in it */
    const currentUrl = window.location.pathname.toLowerCase()
    const isAdminRouteActive = currentUrl.startsWith(urlPrefix + '/admin')
    const isUserRouteActive = currentUrl.startsWith(urlPrefix + '/user')

    /** Preffered application view based on last session and role type */
    let prefferedAppView = getLastViewForApp(userpreferences, user)

    /** Override view mode via URL only if user is admin */
    if (user.role === 'admin') {
      if (isUserRouteActive) {
        prefferedAppView = 'user'
      } else if (isAdminRouteActive) {
        prefferedAppView = 'admin'
      }
      /** Save new app view mode to server */
      const preferenceName = getAppViewPreferenceName(user)
      API.setUserPreferrences(preferenceName, userpreferences, {
        LastViewForApp: prefferedAppView
      })
      /** Fetch and update UserPreferrences in Redux */
      dispatch(userpreferencesSlice.thunk())
    }

    /** Get new set of permissions based on view mode */
    const featureFlags = FeatureFlagsObject.updatePermissionsFor(prefferedAppView)
    setContextValue((s) => ({ ...s, featureFlags, viewMode: prefferedAppView, loading: false }))
    setAppReady(true)
  }

  /**
   * Updates feature flags dynamically from any component.
   * Redirects user to home page of next view
   */
  const updateContext = async (prefferedAppView) => {
    /** Get new set of permissions based on view mode */
    const newFeatureFlags = FeatureFlagsObject.updatePermissionsFor(prefferedAppView)
    /** Save new app view mode to server */
    const preferenceName = getAppViewPreferenceName(user)
    setContextValue((s) => ({
      ...s,
      featureFlags: newFeatureFlags,
      viewMode: prefferedAppView,
      loading: false
    }))
    API.setUserPreferrences(preferenceName, userpreferences, {
      LastViewForApp: prefferedAppView
    }).then(() => {
      /** Fetch and update UserPreferrences in Redux */
      dispatch(userpreferencesSlice.thunk())
    })
    redirectToView(prefferedAppView)
  }

  const prevRoleRef = useRef(null)

  useEffect(() => {
    if (!user?.loggedIn) return
    const roleChanged = prevRoleRef.current !== null && prevRoleRef.current !== user.role
    prevRoleRef.current = user.role
    if (!initiated.current || roleChanged) {
      initiated.current = false
      setRoleBasedPermissions()
    }
  }, [user.loggedIn, user.role])

  /** Keeps viewMode in sync with the active route. The one-time boot check can
   * compute 'user' from a stale preference while the user lands on (or is
   * redirected to) an /admin route — the header chip then shows User@ on admin
   * pages. Re-derive viewMode from the URL prefix whenever the path changes. */
  useEffect(() => {
    if (!appReady || user?.role !== 'admin') return
    const currentPath = (urlPrefix + location.pathname).toLowerCase()
    if (currentPath.startsWith(urlPrefix + '/admin/')) {
      setContextValue((s) => (s.viewMode === 'admin' ? s : { ...s, viewMode: 'admin' }))
    } else if (currentPath.startsWith(urlPrefix + '/user/')) {
      setContextValue((s) => (s.viewMode === 'user' ? s : { ...s, viewMode: 'user' }))
    }
  }, [location.pathname, appReady, user?.role, urlPrefix])

  return (
    <FeatureFlagContext.Provider value={{ data: contextValue, updateContext, appReady }}>
      {!appReady && props.Fallback}
      {appReady && props.children}
    </FeatureFlagContext.Provider>
  )
}
